import React, { useMemo, useRef, useState, useEffect } from "react";
import { Email } from "./hostingerEmailView";
import { Chips, ChipsChangeEvent } from "primereact/chips";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { marked } from "marked";
import { showBasicAlert } from "../../../../core/common/useAlert";
import api from "../../../../services/api";
import "../../../../style/css/email.css";

interface ExtendedEmail extends Email {}

interface EmailDetailProps {
  email: ExtendedEmail;
  allEmails: ExtendedEmail[];
  onBack: () => void;
}

const EmailDetail: React.FC<EmailDetailProps> = ({
  email,
  allEmails,
  onBack,
}) => {
  const [openLightbox, setOpenLightbox] = React.useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isReplyAll, setIsReplyAll] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [subject, setSubject] = useState(` ${email.subject}`);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [ccRecipients, setCcRecipients] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [expandedMessages, setExpandedMessages] = useState<
    Record<string, { message: boolean; quotedText: boolean }>
  >({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [collapsedThreadCount, setCollapsedThreadCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const threadContainerRef = useRef<HTMLDivElement>(null);
  const replyFormRef = useRef<HTMLDivElement>(null);
  const forwardFormRef = useRef<HTMLDivElement>(null);
  const [showForwardForm, setShowForwardForm] = useState(false);
  const [forwardContent, setForwardContent] = useState("");
  const [forwardRecipients, setForwardRecipients] = useState<string[]>([]);
  const [forwardCcRecipients, setForwardCcRecipients] = useState<string[]>([]);
  const [forwardAttachments, setForwardAttachments] = useState<File[]>([]);

  const signature = localStorage.getItem("signature");

  const MAX_ATTACHMENTS = parseInt(
    process.env.REACT_APP_EMAIL_MAX_ATTACHMENTS!,
    10
  );
  const MAX_FILE_SIZE =
    parseInt(process.env.REACT_APP_EMAIL_MAX_FILE_SIZE_MB!, 10) * 1024 * 1024;

  // Get the last email in the thread
  const lastEmail = useMemo(() => {
    const thread = getEmailThread(email, allEmails);
    return thread[thread.length - 1];
  }, [email, allEmails]);

  // Define thread with explicit typing
  const thread = useMemo<ExtendedEmail[]>(() => {
    return getEmailThread(email, allEmails);
  }, [email, allEmails]);

  // Auto-expand the latest message and collapse others on thread change
  // useEffect(() => {
  //   const newExpandedState: Record<
  //     string,
  //     { message: boolean; quotedText: boolean }
  //   > = {};

  //   thread.forEach((msg, index) => {
  //     const isLast = index === thread.length - 1;
  //     newExpandedState[msg.messageId] = {
  //       message: isLast,
  //       quotedText: false,
  //     };
  //   });

  //   if (thread.length > 3) {
  //     setCollapsedThreadCount(thread.length - 2);
  //   } else {
  //     setCollapsedThreadCount(0);
  //   }

  //   setExpandedMessages(newExpandedState);
  //   setAllExpanded(false);

  //   if (threadContainerRef.current) {
  //     threadContainerRef.current.scrollTop =
  //       threadContainerRef.current.scrollHeight;
  //   }
  // }, [thread]);

  useEffect(() => {
    if (!initialLoadComplete) {
      // Initial load logic - set up expansion state
      const newExpandedState: Record<
        string,
        { message: boolean; quotedText: boolean }
      > = {};

      thread.forEach((msg, index) => {
        const isLast = index === thread.length - 1;
        newExpandedState[msg.messageId] = {
          message: isLast,
          quotedText: false,
        };
      });

      setExpandedMessages(newExpandedState);
      setInitialLoadComplete(true);
    } else {
      // During refresh, preserve existing expansion state for messages that still exist
      const newExpandedState: Record<
        string,
        { message: boolean; quotedText: boolean }
      > = {};

      thread.forEach((msg) => {
        // Preserve existing state if message still exists, otherwise use default
        newExpandedState[msg.messageId] = expandedMessages[msg.messageId] || {
          message: false,
          quotedText: false,
        };
      });

      setExpandedMessages(newExpandedState);
    }

    // Update collapsed count
    if (thread.length > 3) {
      setCollapsedThreadCount(thread.length - 2);
    } else {
      setCollapsedThreadCount(0);
    }

    setAllExpanded(false);

    if (threadContainerRef.current) {
      threadContainerRef.current.scrollTop =
        threadContainerRef.current.scrollHeight;
    }
  }, [thread]);

  // Initialize recipients based on reply type
  const getInitialRecipients = (replyAll: boolean) => {
    if (replyAll) {
      const to = lastEmail.from
        .split(",")
        .map((addr) => addr.trim())
        .filter(Boolean);
      const cc = lastEmail.cc
        ? lastEmail.cc
            .split(",")
            .map((addr) => addr.trim())
            .filter((addr) => addr.toLowerCase() !== "unknow cc")
        : [];
      const combined = to;
      const uniqueTo = combined.filter(
        (item, index) => combined.indexOf(item) === index
      );
      return {
        to: uniqueTo,
        cc: cc,
      };
    }
    return {
      to: [lastEmail.from],
      cc: [],
    };
  };

  const handleReply = () => {
    resetForm();
    setIsReplyAll(false);
    const { to, cc } = getInitialRecipients(false);
    setRecipients(parseEmails(to));
    setSubject(` ${lastEmail.subject}`);
    setShowReplyForm(true);
  };

  const handleReplyAll = () => {
    resetForm();
    setIsReplyAll(true);
    const { to, cc } = getInitialRecipients(true);
    setRecipients(parseEmails(to));
    setCcRecipients(parseEmails(cc));
    setSubject(` ${lastEmail.subject}`);
    setShowReplyForm(true);
  };

  const handleForward = () => {
    resetForm();
    setForwardRecipients([]);
    setForwardCcRecipients([]);
    setForwardContent("");
    setForwardAttachments([]);
    setSubject(`Fwd: ${lastEmail.subject}`);
    setShowForwardForm(true);
  };

  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        message: !prev[messageId].message,
      },
    }));
  };

  // New handler to toggle quoted text visibility
  const toggleQuotedText = (messageId: string) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        quotedText: !prev[messageId].quotedText,
      },
    }));
  };

  const toggleAllMessages = () => {
    const expand = !allExpanded;
    const newExpandedState: Record<
      string,
      { message: boolean; quotedText: boolean }
    > = {};
    thread.forEach((msg) => {
      newExpandedState[msg.messageId] = {
        message: expand,
        quotedText: false, // Keep quoted text collapsed even when expanding all
      };
    });
    setExpandedMessages(newExpandedState);
    setAllExpanded(expand);

    if (expand) {
      setCollapsedThreadCount(0);
    } else if (thread.length > 3) {
      setCollapsedThreadCount(thread.length - 2);
    }
  };

  const showCollapsedMessages = () => {
    const newExpandedState: Record<
      string,
      { message: boolean; quotedText: boolean }
    > = {};
    thread.forEach((msg) => {
      newExpandedState[msg.messageId] = {
        message: true,
        quotedText: false, // Keep quoted text collapsed
      };
    });
    setExpandedMessages(newExpandedState);
    setAllExpanded(true);
    setCollapsedThreadCount(0);
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const customChip = (item: string) => {
    const isValid = validateEmail(item);
    return (
      <div
        className={`p-chip p-component ${
          isValid ? "p-chip-valid" : "p-chip-invalid"
        }`}
        style={{
          background: isValid ? "#e8f0fe" : "#fce8e6",
          color: isValid ? "#1a73e8" : "#d93025",
          borderRadius: "16px",
          padding: "2px 8px",
          margin: "2px",
          display: "inline-flex",
          alignItems: "center",
          fontSize: "14px",
        }}
      >
        <span>{item}</span>
      </div>
    );
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentValues: string[],
    setValues: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (e.key === " " || e.key === "Enter") {
      const inputElement = e.target as HTMLInputElement;
      const value = inputElement.value.trim();
      if (value && value !== "Unknown Recipient" && value !== "Unknown cc") {
        setValues([...currentValues, value]);
        inputElement.value = "";
        e.preventDefault();
      }
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    currentValues: string[],
    setValues: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const value = e.target.value.trim();
    if (value && value !== "Unknown Recipient" && value !== "Unknown cc") {
      setValues([...currentValues, value]);
      e.target.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);

      if (attachments.length + newFiles.length > MAX_ATTACHMENTS) {
        showBasicAlert(`You can attach maximum ${MAX_ATTACHMENTS} files`);
        return;
      }

      for (const file of newFiles) {
        if (file.size > MAX_FILE_SIZE) {
          showBasicAlert(
            `File ${file.name} is too large (max ${
              MAX_FILE_SIZE / 1024 / 1024
            }MB)`
          );
          return;
        }
      }

      setAttachments((prevAttachments) => [...prevAttachments, ...newFiles]);
      e.target.value = "";
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    const invalidEmails = [
      ...recipients.filter((email) => !validateEmail(email)),
      ...ccRecipients.filter((email) => !validateEmail(email)),
    ];

    if (invalidEmails.length > 0) {
      showBasicAlert(
        "Please correct invalid email addresses: " + invalidEmails.join(", ")
      );
      setIsSending(false);
      return;
    }

    const formData = new FormData();
    formData.append("to", JSON.stringify(recipients));
    formData.append("cc", JSON.stringify(ccRecipients));
    formData.append("subject", subject);
    formData.append("message", replyContent);
    formData.append("inReplyTo", lastEmail.messageId);
    formData.append(
      "references",
      JSON.stringify([...lastEmail.references, lastEmail.messageId])
    );
    attachments.forEach((file) => {
      formData.append("attachments", file);
    });
    formData.append("signature", signature || "");
    try {
      await api.post("mailService/replyToMailBox", formData);
      showBasicAlert("Reply sent successfully!");
      resetForm();
    } catch (error) {
      console.error("Failed to send reply:", error);
      showBasicAlert("Failed to send reply. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleSendForward = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    const invalidEmails = [
      ...forwardRecipients.filter((email) => !validateEmail(email)),
      ...forwardCcRecipients.filter((email) => !validateEmail(email)),
    ];

    if (invalidEmails.length > 0) {
      showBasicAlert(
        "Please correct invalid email addresses: " + invalidEmails.join(", ")
      );
      setIsSending(false);
      return;
    }

    const formData = new FormData();
    formData.append("to", JSON.stringify(forwardRecipients));
    formData.append("cc", JSON.stringify(forwardCcRecipients));
    formData.append("subject", `Fwd: ${lastEmail.subject}`);
    formData.append("originalMessage", lastEmail.text);
    formData.append("message", forwardContent);

    if (lastEmail.attachments && lastEmail.attachments.length > 0) {
      lastEmail.attachments.forEach((attachment) => {
        const byteCharacters = atob(attachment.content);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = Array.from(slice).map((char) =>
            char.charCodeAt(0)
          );
          byteArrays.push(new Uint8Array(byteNumbers));
        }

        const blob = new Blob(byteArrays, { type: "application/octet-stream" });
        formData.append("attachments", new File([blob], attachment.filename));
      });
    }

    forwardAttachments.forEach((file) => {
      formData.append("attachments", file);
    });
    formData.append("signature", signature || "");
    try {
      await api.post("mailService/forwardEmailHandler", formData);
      showBasicAlert("Email forwarded successfully!");
      setShowForwardForm(false);
    } catch (error) {
      console.error("Failed to forward email:", error);
      showBasicAlert("Failed to forward email. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const downloadBase64Attachment = (attachment: any) => {
    try {
      if (!attachment || !attachment.content) {
        console.error("Invalid attachment");
        return;
      }

      const byteCharacters = atob(attachment.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.contentType });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      showBasicAlert("Failed to download attachment. Please try again.");
    }
  };

  const checkAndCloseForm = () => {
    resetForm();
  };

  useEffect(() => {
    if (showReplyForm && replyFormRef.current) {
      replyFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    if (showForwardForm && forwardFormRef.current) {
      forwardFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [showReplyForm, showForwardForm]);

  const resetForm = () => {
    setShowReplyForm(false);
    setShowForwardForm(false);
    setRecipients([]);
    setCcRecipients([]);
    setReplyContent("");
    setAttachments([]);
    setForwardRecipients([]);
    setForwardCcRecipients([]);
    setForwardContent("");
    setForwardAttachments([]);
  };

  function getEmailThread(email: ExtendedEmail, allEmails: ExtendedEmail[]) {
    const messageIdMap = new Map<string, ExtendedEmail>();
    allEmails.forEach((e) => messageIdMap.set(e.messageId, e));

    const includedEmails = new Set<string>();

    const collectThread = (
      currentEmail: ExtendedEmail,
      thread: ExtendedEmail[]
    ) => {
      if (includedEmails.has(currentEmail.messageId)) return;

      thread.push(currentEmail);
      includedEmails.add(currentEmail.messageId);

      const replies = allEmails.filter(
        (e) =>
          e.inReplyTo === currentEmail.messageId ||
          (e.references && e.references.includes(currentEmail.messageId))
      );

      replies.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      replies.forEach((reply) => collectThread(reply, thread));
    };

    let rootEmail = email;
    while (rootEmail.inReplyTo && messageIdMap.has(rootEmail.inReplyTo)) {
      rootEmail = messageIdMap.get(rootEmail.inReplyTo)!;
    }

    if (email.references && email.references.length > 0) {
      const firstReference = email.references[0];
      if (messageIdMap.has(firstReference)) {
        const potentialRoot = messageIdMap.get(firstReference)!;
        if (new Date(potentialRoot.date) < new Date(rootEmail.date)) {
          rootEmail = potentialRoot;
        }
      }
    }

    const thread: ExtendedEmail[] = [];
    collectThread(rootEmail, thread);

    if (thread.length === 1) {
      const referencingEmails = allEmails.filter(
        (e) => e.references && e.references.includes(thread[0].messageId)
      );
      referencingEmails.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      referencingEmails.forEach((e) => collectThread(e, thread));
    }

    return thread.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }

  const parseEmails = (rawEmails: string[]): string[] =>
    rawEmails
      .flatMap((emailString) => emailString.split(","))
      .map((email) => {
        const emailMatch = email.match(/<([^>]+)>/);
        return emailMatch ? emailMatch[1] : email.replace(/"/g, "").trim();
      });

  function cleanEmailText(text: string, isOriginal: boolean): string {
    if (isOriginal) return text;
    const isForwarded = text.includes(
      "---------- Forwarded message ----------"
    );

    if (isForwarded) {
      return text;
    }
    const patterns = [
      /On[\s\S]*?wrote:[\s\S]*$/,
      /From:[\s\S]*$/,
      /To:[\s\S]*$/,
      /Date:[\s\S]*$/,
      /Subject:[\s\S]*$/,
      /-----Original Message-----[\s\S]*$/,
      /On[\s\S]*?<[\s\S]*?>[\s\S]*?wrote:[\s\S]*$/,
      />[^\n]*/g,
      /Reply[\s\S]*?above this line[\s\S]*$/,
      /^.*On.*wrote:.*$/gm,
      /^.*From:.*$/gm,
      /^.*To:.*$/gm,
      /^.*Date:.*$/gm,
      /^.*Subject:.*$/gm,
      /\n-+\s*Original Message\s*-+[\s\S]*$/i,
      /\n-+\s*Forwarded message\s*-+[\s\S]*$/i,
    ];

    let cleanText = text;
    patterns.forEach((pattern) => {
      cleanText = cleanText.replace(pattern, "");
    });

    cleanText = cleanText.replace(/\n+$/, "");
    return cleanText.trim();
  }

  function formatSender(raw: string) {
    const match = raw.match(/^(.*)<(.*)>$/);
    if (match) {
      const name = match[1].trim().replace(/(^"|"$)/g, "");
      const email = match[2].trim();
      return `${name} <${email}>`;
    }
    return raw;
  }

  const messageIdMap = useMemo(() => {
    const map = new Map<string, ExtendedEmail>();
    allEmails.forEach((e) => {
      if (e.messageId) {
        map.set(e.messageId, e);
      }
    });
    return map;
  }, [allEmails]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitials = (name: string) => {
    const fromName = name.split("<")[0].trim() || name;
    const names = fromName.split(" ");
    return names
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getPreviewText = (text: string) => {
    const cleanText = cleanEmailText(text, false);
    const lines = cleanText.split("\n");
    let preview = "";

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (
        trimmedLine &&
        !trimmedLine.match(
          /^(hi|hello|dear|thanks|regards|best|cheers),?\s+/i
        ) &&
        !trimmedLine.match(/^[a-z]+\s*,$/i)
      ) {
        preview = trimmedLine;
        break;
      }
    }

    if (!preview) {
      preview = cleanText.substring(0, 100);
    } else if (preview.length > 100) {
      preview = preview.substring(0, 100) + "...";
    }

    return preview || "[No message content]";
  };

  const extractReplyContent = (text: string): string => {
    const quotePatterns = [
      /On.*[\r\n]+.*wrote:[\s\S]*$/,
      /From:.*[\r\n]+.*To:.*[\r\n]+.*Subject:.*[\s\S]*$/,
      /-----Original Message-----[\s\S]*$/,
      /On.*<.*>.*wrote:[\s\S]*$/,
      />.*/g,
    ];

    let replyText = text;
    let earliestIndex = text.length;
    quotePatterns.forEach((pattern) => {
      const match = text.match(pattern);
      if (match && match.index !== undefined && match.index < earliestIndex) {
        earliestIndex = match.index;
      }
    });

    if (earliestIndex < text.length) {
      replyText = text.substring(0, earliestIndex).trim();
    }

    return replyText;
  };

  const renderQuotedText = (
    text: string,
    originalEmail: Email,
    messageId: string
  ) => {
    const quotePatterns = [
      /On.*[\r\n]+.*wrote:[\s\S]*$/,
      /From:.*[\r\n]+.*To:.*[\r\n]+.*Subject:.*[\s\S]*$/,
      /-----Original Message-----[\s\S]*$/,
      /On.*<.*>.*wrote:[\s\S]*$/,
    ];

    for (const pattern of quotePatterns) {
      const match = text.match(pattern);
      if (match) {
        const parsedContent = marked.parse(
          cleanEmailText(originalEmail.text, true)
        );
        const content = typeof parsedContent === "string" ? parsedContent : "";
        const isQuotedTextExpanded =
          expandedMessages[messageId]?.quotedText || false;

        return (
          <div className="quoted-text-container">
            {!isQuotedTextExpanded ? (
              <div
                className="quoted-text-toggle"
                onClick={() => toggleQuotedText(messageId)}
                title="Show quoted text"
              >
                <i className="ti ti-dots" />
              </div>
            ) : (
              <div className="quoted-text p-3 bg-light rounded">
                <div className="quoted-header mb-2">
                  <small className="text-muted">
                    On {formatDate(originalEmail.date)},{" "}
                    {formatSender(originalEmail.from)} wrote:
                  </small>
                  <button
                    className="btn btn-sm btn-link p-0"
                    onClick={() => toggleQuotedText(messageId)}
                    aria-label="Collapse quoted text"
                  >
                    <i className="ti ti-chevron-up" />
                  </button>
                </div>
                <div
                  className="quoted-content"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </div>
            )}
          </div>
        );
      }
    }

    return null;
  };

  return (
    <div className="mail-detail bg-white border-bottom p-3 w-100 h-100 d-flex flex-column">
      <div className="d-flex flex-column h-100">
        <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 border-bottom mb-3 pb-3">
          <div className="dropdown">
            <button className="btn btn-sm btn-outline-primary" onClick={onBack}>
              <i className="ti ti-arrow-left me-2" /> Back
            </button>
          </div>
          <div className="d-flex align-items-center">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={toggleAllMessages}
            >
              <i
                className={`ti ${
                  allExpanded ? "ti-chevrons-up" : "ti-chevrons-down"
                } me-1`}
              />
              {allExpanded ? "Collapse All" : "Expand All"}
            </button>
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto" ref={threadContainerRef}>
          {thread.map((msg, index) => {
            const isOriginal = index === 0;
            const isLast = index === thread.length - 1;
            const cleanText = extractReplyContent(msg.text);
            const isExpanded =
              expandedMessages[msg.messageId]?.message || isLast;
            const hasAttachments =
              msg.attachments && msg.attachments.length > 0;

            if (
              thread.length > 3 &&
              !allExpanded &&
              index > 0 &&
              index < thread.length - 1 &&
              !isExpanded
            ) {
              return null;
            }

            return (
              <div
                key={msg.messageId || index}
                className={`email-message mb-3 rounded ${
                  isExpanded ? "expanded" : "collapsed"
                } ${isOriginal ? "bg-light" : "bg-white"}`}
                style={{
                  borderLeft: `3px solid ${isOriginal ? "#4e73df" : "#dddfeb"}`,
                }}
              >
                <div
                  className="message-header p-3 d-flex justify-content-between align-items-center cursor-pointer"
                  onClick={() =>
                    !isLast && toggleMessageExpansion(msg.messageId)
                  }
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-sm me-3">
                      {msg.profileImage ? (
                        <ImageWithBasePath
                          src={msg.profileImage}
                          alt="Profile"
                        />
                      ) : (
                        <div className="avatar-initials bg-secondary text-white">
                          {getInitials(msg.from)}
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center">
                        <h6 className="mb-0">
                          {msg.from.split("<")[0].trim() || msg.from}
                        </h6>
                        <small className="text-muted ms-2">
                          {formatDate(msg.date)}
                          {hasAttachments && !isExpanded && (
                            <span className="ms-2">
                              <i className="ti ti-paperclip" />{" "}
                              {msg.attachments.length}
                            </span>
                          )}
                        </small>
                      </div>
                      {!isExpanded && (
                        <p className="mb-0 text-muted preview-text">
                          {getPreviewText(msg.text)}
                        </p>
                      )}
                    </div>
                  </div>
                  {!isLast && (
                    <button
                      className="btn btn-sm btn-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMessageExpansion(msg.messageId);
                      }}
                    >
                      {isExpanded ? (
                        <i className="ti ti-chevron-up" />
                      ) : (
                        <i className="ti ti-chevron-down" />
                      )}
                    </button>
                  )}
                </div>
                {isExpanded && (
                  <div className="message-content p-3">
                    {!isOriginal &&
                      msg.inReplyTo &&
                      messageIdMap.has(msg.inReplyTo) && (
                        <div className="text-muted fs-12 mb-2">
                          ↪️ Replying to:{" "}
                          <strong>
                            {formatSender(
                              messageIdMap.get(msg.inReplyTo)!.from
                            )}
                          </strong>
                        </div>
                      )}

                    <div className="mb-3">
                      <p className="fs-12 text-dark mb-1">
                        <strong>To:</strong> {msg.to}
                      </p>
                      <p className="fs-12 text-dark mb-1">
                        <strong>From:</strong> {msg.from}
                      </p>
                      <p className="fs-12 text-dark mb-1">
                        <strong>Subject:</strong> {msg.subject}
                      </p>
                      {msg.cc &&
                        msg.cc.trim().toLowerCase() !== "unknow cc" && (
                          <p className="fs-12 text-dark mb-1">
                            <strong>Cc:</strong> {msg.cc}
                          </p>
                        )}
                    </div>

                    <div
                      className="email-body"
                      dangerouslySetInnerHTML={{
                        __html: marked.parse(cleanText),
                      }}
                    />
                    {msg.text.includes(
                      "---------- Forwarded message ----------"
                    ) && (
                      <div className="forwarded-message mt-3 p-3 bg-light rounded">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: marked.parse(
                              msg.text.replace(
                                /^[\s\S]*---------- Forwarded message ----------/,
                                "---------- Forwarded message ----------"
                              )
                            ),
                          }}
                        />
                      </div>
                    )}
                    {!isOriginal &&
                      msg.inReplyTo &&
                      messageIdMap.has(msg.inReplyTo) &&
                      renderQuotedText(
                        msg.text,
                        messageIdMap.get(msg.inReplyTo)!,
                        msg.messageId
                      )}

                    {hasAttachments && (
                      <div className="mt-3 pt-3 border-top">
                        <h6 className="fs-14 mb-2">
                          <i className="ti ti-paperclip me-2" />
                          Attachments ({msg.attachments.length})
                        </h6>
                        <div className="d-flex flex-wrap gap-2">
                          {msg.attachments.map((attachment, index) => (
                            <div key={index} className="attachment-thumbnail">
                              <div className="d-flex flex-column align-items-center">
                                {attachment.contentType?.startsWith(
                                  "image/"
                                ) ? (
                                  <img
                                    src={`data:${attachment.contentType};base64,${attachment.content}`}
                                    alt={attachment.filename}
                                    className="img-thumbnail mb-2"
                                    style={{
                                      maxHeight: "100px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => setOpenLightbox(true)}
                                  />
                                ) : (
                                  <div className="attachment-icon mb-2">
                                    <i
                                      className="ti ti-file"
                                      style={{ fontSize: "2rem" }}
                                    />
                                  </div>
                                )}
                                <small
                                  className="d-block text-truncate"
                                  style={{ maxWidth: "120px" }}
                                >
                                  {attachment.filename}
                                </small>
                                <small className="text-muted">
                                  {(attachment.size / 1024).toFixed(2)} KB
                                </small>
                                <button
                                  className="btn btn-sm btn-primary mt-2"
                                  onClick={() =>
                                    downloadBase64Attachment(attachment)
                                  }
                                >
                                  <i className="ti ti-download me-1" />
                                  Download
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {isLast && (
                      <div className="d-flex justify-content-end mb-3 gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleReply}
                        >
                          <i className="ti ti-reply me-2" /> Reply
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleReplyAll}
                        >
                          <i className="ti ti-reply-all me-2" /> Reply All
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleForward}
                        >
                          <i className="ti ti-forward me-2" /> Forward
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {collapsedThreadCount > 0 && (
            <div className="text-center py-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={showCollapsedMessages}
              >
                {collapsedThreadCount} earlier messages
                <i className="ti ti-chevron-down ms-2" />
              </button>
            </div>
          )}

          {showReplyForm && (
            <div
              className="card shadow-none mt-3 reply-form-enter"
              ref={replyFormRef}
            >
              <div className="card-body">
                <form onSubmit={handleSendReply} encType="multipart/form-data">
                  <div className="rounded">
                    <div
                      className="p-3 position-relative pb-2 border-bottom chip-with-image d-flex justify-content-between align-items-start"
                      style={{ height: "auto", flexWrap: "wrap" }}
                    >
                      {" "}
                      <div className="tag-with-img d-flex align-items-center">
                        <label className="form-label me-2">To</label>
                        <div
                          className="chip-scroll flex-grow-1"
                          style={{ height: "auto", overflow: "visible" }}
                        >
                          <Chips
                            value={recipients}
                            className="input-tags form-control border-0 h-100 w-100"
                            onChange={(e: ChipsChangeEvent) =>
                              setRecipients(e.value || [])
                            }
                            onKeyDown={(e) =>
                              handleKeyDown(e, recipients, setRecipients)
                            }
                            onBlur={(e) =>
                              handleBlur(e, recipients, setRecipients)
                            }
                            itemTemplate={customChip}
                            placeholder="Enter email addresses"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="position-absolute top-0 end-0 m-2 btn btn-icon btn-sm rounded-circle"
                        onClick={checkAndCloseForm}
                        aria-label="Close reply form"
                        disabled={isSending}
                      >
                        <i className="ti ti-x" />
                      </button>
                    </div>
                    <div className="p-3 position-relative pb-2 border-bottom chip-with-image">
                      <div className="tag-with-img d-flex align-items-center">
                        <label className="form-label me-2">Cc</label>
                        <div
                          className="chip-scroll flex-grow-1 overflow-auto"
                          style={{ maxHeight: "100px" }}
                        >
                          <Chips
                            value={ccRecipients}
                            className="input-tags form-control border-0 h-100 w-100"
                            onChange={(e: ChipsChangeEvent) =>
                              setCcRecipients(e.value || [])
                            }
                            onKeyDown={(e) =>
                              handleKeyDown(e, ccRecipients, setCcRecipients)
                            }
                            onBlur={(e) =>
                              handleBlur(e, ccRecipients, setCcRecipients)
                            }
                            itemTemplate={customChip}
                            placeholder="Enter Cc email addresses"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="mb-3">
                        <textarea
                          rows={7}
                          className="form-control border-0 p-0"
                          placeholder="Type your reply here..."
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          disabled={isSending}
                        />
                      </div>
                      {attachments.length > 0 && (
                        <div className="p-3 border-bottom">
                          <div className="d-flex flex-wrap gap-2">
                            {attachments.map((file, index) => (
                              <div
                                key={index}
                                className="d-flex align-items-center bg-light p-2 rounded"
                              >
                                <span className="me-2">{file.name}</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger p-0"
                                  onClick={() => removeAttachment(index)}
                                  disabled={isSending}
                                >
                                  <i className="ti ti-x" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center justify-content-between border-top p-3">
                      <div className="d-flex align-items-center">
                        <button
                          type="submit"
                          className="btn btn-primary d-inline-flex align-items-center"
                          disabled={isSending}
                        >
                          {isSending ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Sending...
                            </>
                          ) : (
                            <>Send</>
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-icon btn-sm rounded-circle me-2"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isSending}
                        >
                          <i
                            className="ti ti-paperclip"
                            style={{ fontSize: "20px", marginLeft: "10px" }}
                          />
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            multiple
                            className="d-none"
                            disabled={isSending}
                          />
                        </button>
                      </div>
                      <div className="d-flex align-items-center">
                        <button
                          type="button"
                          className="btn btn-outline-secondary me-2"
                          onClick={checkAndCloseForm}
                          disabled={isSending}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
          {showForwardForm && (
            <div
              className="card shadow-none mt-3 reply-form-enter"
              ref={forwardFormRef}
            >
              <div className="card-body">
                <form
                  onSubmit={handleSendForward}
                  encType="multipart/form-data"
                >
                  <div className="rounded">
                    <div
                      className="p-3 position-relative pb-2 border-bottom chip-with-image d-flex justify-content-between align-items-start"
                      style={{ height: "auto", flexWrap: "wrap" }}
                    >
                      {" "}
                      <div className="tag-with-img d-flex align-items-center">
                        <label className="form-label me-2">To</label>
                        <div
                          className="chip-scroll flex-grow-1"
                          style={{ height: "auto", overflow: "visible" }}
                        >
                          <Chips
                            value={forwardRecipients}
                            className="email-chips-input w-100"
                            onChange={(e: ChipsChangeEvent) =>
                              setForwardRecipients(e.value || [])
                            }
                            onKeyDown={(e) =>
                              handleKeyDown(
                                e,
                                forwardRecipients,
                                setForwardRecipients
                              )
                            }
                            onBlur={(e) =>
                              handleBlur(
                                e,
                                forwardRecipients,
                                setForwardRecipients
                              )
                            }
                            itemTemplate={customChip}
                            placeholder="Enter email addresses"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="position-absolute top-0 end-0 m-2 btn btn-icon btn-sm rounded-circle"
                        onClick={() => setShowForwardForm(false)}
                        aria-label="Close forward form"
                        disabled={isSending}
                      >
                        <i className="ti ti-x" />
                      </button>
                    </div>
                    <div>
                      <div className="p-3 position-relative pb-2 border-bottom chip-with-image">
                        <div className="tag-with-img d-flex align-items-center">
                          <label className="form-label me-2">Cc</label>
                          <div
                            className="chip-scroll flex-grow-1"
                            style={{ height: "auto", overflow: "visible" }}
                          >
                            <Chips
                              value={forwardCcRecipients}
                              className="email-chips-input w-100"
                              onChange={(e: ChipsChangeEvent) =>
                                setForwardCcRecipients(e.value || [])
                              }
                              onKeyDown={(e) =>
                                handleKeyDown(
                                  e,
                                  forwardCcRecipients,
                                  setForwardCcRecipients
                                )
                              }
                              onBlur={(e) =>
                                handleBlur(
                                  e,
                                  forwardCcRecipients,
                                  setForwardCcRecipients
                                )
                              }
                              itemTemplate={customChip}
                              placeholder="Enter Cc email addresses"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="mb-3">
                        <textarea
                          rows={7}
                          className="form-control border-0 p-0"
                          placeholder="Add a message (optional)..."
                          value={forwardContent}
                          onChange={(e) => setForwardContent(e.target.value)}
                          disabled={isSending}
                        />
                      </div>
                      <div className="p-3 bg-light rounded mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted">
                            Forwarded message
                          </small>
                        </div>
                        <div className="border-start ps-3">
                          <div
                            className="email-body mt-2"
                            dangerouslySetInnerHTML={{
                              __html: marked.parse(
                                lastEmail.text.includes(
                                  "---------- Forwarded message ----------"
                                )
                                  ? lastEmail.text
                                  : `---------- Forwarded message ----------\n` +
                                      `From: ${formatSender(
                                        lastEmail.from
                                      )}\n` +
                                      `Date: ${formatDate(lastEmail.date)}\n` +
                                      `Subject: ${lastEmail.subject}\n` +
                                      `To: ${lastEmail.to}\n\n` +
                                      `${cleanEmailText(lastEmail.text, true)}`
                              ),
                            }}
                          />
                        </div>
                      </div>
                      {(forwardAttachments.length > 0 ||
                        (lastEmail.attachments &&
                          lastEmail.attachments.length > 0)) && (
                        <div className="p-3 border-bottom">
                          <h6 className="fs-14 mb-2">
                            <i className="ti ti-paperclip me-2" />
                            Attachments (
                            {forwardAttachments.length +
                              (lastEmail.attachments?.length || 0)}
                            )
                          </h6>
                          <div className="d-flex flex-wrap gap-2">
                            {lastEmail.attachments?.map((attachment, index) => (
                              <div
                                key={`orig-${index}`}
                                className="attachment-thumbnail"
                              >
                                <div className="d-flex flex-column align-items-center">
                                  {attachment.contentType?.startsWith(
                                    "image/"
                                  ) ? (
                                    <img
                                      src={`data:${attachment.contentType};base64,${attachment.content}`}
                                      alt={attachment.filename}
                                      className="img-thumbnail mb-2"
                                      style={{ maxHeight: "100px" }}
                                    />
                                  ) : (
                                    <div className="attachment-icon mb-2">
                                      <i
                                        className="ti ti-file"
                                        style={{ fontSize: "2rem" }}
                                      />
                                    </div>
                                  )}
                                  <small
                                    className="d-block text-truncate"
                                    style={{ maxWidth: "120px" }}
                                  >
                                    {attachment.filename}
                                  </small>
                                  <small className="text-muted">
                                    {(attachment.size / 1024).toFixed(2)} KB
                                  </small>
                                </div>
                              </div>
                            ))}
                            {forwardAttachments.map((file, index) => (
                              <div
                                key={`new-${index}`}
                                className="d-flex align-items-center bg-light p-2 rounded"
                              >
                                <span className="me-2">{file.name}</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger p-0"
                                  onClick={() => {
                                    setForwardAttachments(
                                      forwardAttachments.filter(
                                        (_, i) => i !== index
                                      )
                                    );
                                  }}
                                  disabled={isSending}
                                >
                                  <i className="ti ti-x" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center justify-content-between border-top p-3">
                      <div className="d-flex align-items-center">
                        <button
                          type="submit"
                          className="btn btn-primary d-inline-flex align-items-center"
                          disabled={isSending}
                        >
                          {isSending ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Sending...
                            </>
                          ) : (
                            <>Send</>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-icon btn-sm rounded-circle me-2"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isSending}
                        >
                          <i
                            className="ti ti-paperclip"
                            style={{ fontSize: "20px", marginLeft: "10px" }}
                          />
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={(e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                const newFiles = Array.from(e.target.files);
                                if (
                                  forwardAttachments.length + newFiles.length >
                                  MAX_ATTACHMENTS
                                ) {
                                  showBasicAlert(
                                    `You can attach maximum ${MAX_ATTACHMENTS} files`
                                  );
                                  return;
                                }
                                setForwardAttachments([
                                  ...forwardAttachments,
                                  ...newFiles,
                                ]);
                                e.target.value = "";
                              }
                            }}
                            multiple
                            className="d-none"
                            disabled={isSending}
                          />
                        </button>
                      </div>
                      <div className="d-flex align-items-center">
                        <button
                          type="button"
                          className="btn btn-outline-secondary me-2"
                          onClick={() => setShowForwardForm(false)}
                          disabled={isSending}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={
          lastEmail.attachments
            ?.filter((a) => a.type?.startsWith("image/"))
            .map((a) => ({ src: a.url })) || []
        }
      /> */}
    </div>
  );
};

export default EmailDetail;
